import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { fieldPadding, fieldSurface } from './index';
import { COUNTRIES, flagEmoji, searchCountries, type Country } from '@/lib/countries';

interface PhoneInputProps {
  /** Id for the national-number input, so a `<label for>` reaches it. */
  id: string;
  country: Country;
  onCountryChange: (country: Country) => void;
  /** Spread from react-hook-form's `register`. */
  inputProps: ComponentPropsWithRef<'input'>;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
}

/**
 * A phone field with an international dialling-code picker.
 *
 * The picker is a listbox in a popover rather than a `<select>`: a native select cannot
 * hold a search box, and 240 options without search is unusable. It follows the ARIA
 * combobox pattern — the search input owns `aria-activedescendant` while the list
 * renders `role="option"` rows — so the keyboard behaviour a screen-reader user expects
 * (type to filter, arrows to move, Enter to choose, Escape to dismiss) is what happens.
 */
export function PhoneInput({
  id,
  country,
  onCountryChange,
  inputProps,
  invalid,
  describedBy,
  className,
}: PhoneInputProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const listId = useId();
  const optionId = (index: number): string => `${listId}-option-${index}`;

  /**
   * Filtering is memoised on the query alone.
   *
   * Without this, every keystroke re-scores 240 rows twice — once for the render that
   * sets the query and once for the render that follows the `active` reset.
   */
  const results = useMemo(() => searchCountries(query), [query]);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setQuery('');
    setActive(0);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const choose = useCallback(
    (next: Country) => {
      onCountryChange(next);
      close(true);
    },
    [close, onCountryChange],
  );

  // Focus the search box on open, so typing filters immediately.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Dismiss on a click outside, and on the page scrolling away underneath.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  // Keep the highlighted row in view as the arrows move it.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`#${CSS.escape(optionId(active))}`)
      ?.scrollIntoView({ block: 'nearest' });
    // `optionId` is stable for a given render; `active` and `open` are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, open]);

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
        break;
      case 'Home':
        event.preventDefault();
        setActive(0);
        break;
      case 'End':
        event.preventDefault();
        setActive(Math.max(0, results.length - 1));
        break;
      case 'Enter': {
        event.preventDefault();
        const picked = results[active];
        if (picked) choose(picked);
        break;
      }
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'Tab':
        // Let focus leave, but do not leave an orphaned popover behind it.
        close(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {/*
        The surface comes from the same constant `Input` uses, so the phone field is the
        same height, radius and fill as the email box beside it. Only the padding moves:
        it goes on the two children instead of the box, because they need a divider
        between them.
      */}
      <div
        aria-invalid={invalid}
        className={cn(
          'flex w-full items-stretch overflow-hidden',
          fieldSurface,
          invalid ? '' : 'focus-within:border-primary focus-within:bg-surface',
        )}
      >
        <button
          ref={triggerRef}
          type="button"
          // The visible content is a flag and a code; neither reads as a country to a
          // screen reader, so the name is spelled out.
          aria-label={`Country calling code: ${country.name}, +${country.dial}. Change`}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            // `px-3`, not the shared `fieldPadding`: the trigger and the number share one
            // box, so every pixel of padding here is a pixel the number loses — and on a
            // two-up row with the email field the placeholder was being clipped.
            'flex shrink-0 items-center gap-1 border-r border-outline-strong/40 px-3 py-2.5',
            // Same type as `fieldBase`. On `text-body-md` the trigger's 1.7 leading made
            // it 2px taller than the number beside it, and the whole field 2px taller than
            // the email box above.
            'text-[15px] font-medium leading-normal text-heading transition-colors',
            'hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
          )}
        >
          <span aria-hidden="true" className="text-[15px] leading-none">
            {flagEmoji(country.iso2)}
          </span>
          <span className="tabular-nums">+{country.dial}</span>
          <ChevronDown aria-hidden="true" className="size-3.5 text-muted" />
        </button>

        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="50 1234567"
          aria-invalid={invalid}
          aria-describedby={describedBy}
          {...inputProps}
          className={cn(
            // Matches `fieldBase`, which the email box beside it uses. On `text-body-md`
            // its 1.7 leading made this field 2px taller than every other one.
            'min-w-0 flex-1 bg-transparent text-[15px] leading-normal text-heading outline-none placeholder:text-muted/60',
            fieldPadding,
          )}
        />
      </div>

      {open ? (
        <div className="absolute left-0 top-full z-30 mt-2 w-full min-w-[17rem] overflow-hidden rounded-card border border-outline bg-surface-raised shadow-lifted">
          <div className="flex items-center gap-2 border-b border-outline px-3 py-2.5">
            <Search aria-hidden="true" className="size-4 shrink-0 text-muted" />
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={results.length > 0 ? optionId(active) : undefined}
              aria-label="Search countries by name or calling code"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onSearchKeyDown}
              placeholder="Country or code — try 971 or India"
              className="min-w-0 flex-1 bg-transparent text-sm text-heading outline-none placeholder:text-muted"
            />
          </div>

          {/*
            `aria-live` on the count, because filtering a list the user cannot see is
            otherwise silent — they type and nothing is announced.
          */}
          <p aria-live="polite" className="sr-only">
            {results.length} {results.length === 1 ? 'country' : 'countries'} available
          </p>

          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Country calling codes"
            className="max-h-64 overflow-y-auto py-1"
          >
            {results.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-muted">
                No country matches “{query}”.
              </li>
            ) : (
              results.map((item, index) => {
                const selected = item.iso2 === country.iso2;
                return (
                  <li
                    key={item.iso2}
                    id={optionId(index)}
                    role="option"
                    aria-selected={selected}
                    /*
                      `content-visibility` lets the browser skip laying out rows that are
                      scrolled out of view. With 240 options that is most of them, and it
                      buys the saving without a virtualisation library or the keyboard
                      and screen-reader bugs one brings. `contain-intrinsic-size` keeps
                      the scrollbar honest for the rows it skipped.
                    */
                    className="cv-auto"
                    onMouseEnter={() => setActive(index)}
                    onClick={() => choose(item)}
                  >
                    <span
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm',
                        index === active ? 'bg-surface-wash' : '',
                        selected ? 'font-semibold text-primary-strong' : 'text-heading',
                      )}
                    >
                      <span aria-hidden="true" className="w-6 shrink-0 text-base leading-none">
                        {flagEmoji(item.iso2)}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      <span className="shrink-0 tabular-nums text-muted">+{item.dial}</span>
                      {selected ? (
                        <Check aria-hidden="true" className="size-4 shrink-0 text-primary" />
                      ) : null}
                    </span>
                  </li>
                );
              })
            )}
          </ul>

          <p className="border-t border-outline px-3 py-2 text-[11px] text-muted">
            {COUNTRIES.length} countries · search by name or code
          </p>
        </div>
      ) : null}
    </div>
  );
}
